/**
 * CalculationPhotoSlider — kompakter Bilder-Slider mit fix kleinen
 * Thumbnails über dem DAT-Iframe in der Kalkulation.
 *
 * Klick → kompaktes Mini-Modal (max 60vw × 60vh) statt Vollbild-Lightbox,
 * damit die DAT-Kalkulation darunter sichtbar bleibt.
 */
import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImageOff, Camera, Maximize2, X, ZoomIn, ZoomOut } from "lucide-react";
import AuthImg from "./AuthImg";
import { useAuth } from "@/lib/auth";

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

export default function CalculationPhotoSlider({ photos = [] }) {
  const { accessToken } = useAuth();
  const scrollerRef = useRef(null);
  const [openIndex, setOpenIndex] = useState(null);
  const [zoom, setZoom] = useState(1);

  // Helper: open a photo at index AND reset zoom in one go.
  const openAt = (i) => {
    setOpenIndex(i);
    setZoom(1);
  };

  // Keyboard nav inside mini-modal
  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenIndex(null);
      else if (e.key === "ArrowLeft") openAt((openIndex - 1 + photos.length) % photos.length);
      else if (e.key === "ArrowRight") openAt((openIndex + 1) % photos.length);
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
      else if (e.key === "-") setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
      else if (e.key === "0") setZoom(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIndex, photos.length]);

  if (!photos || photos.length === 0) {
    return (
      <div
        className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/60 p-6 flex items-center gap-3 text-zinc-500"
        data-testid="calc-photo-slider-empty"
      >
        <ImageOff className="h-5 w-5" />
        <div className="text-sm">
          Keine Lichtbilder vorhanden — wechsle in den <strong>Fotos</strong>-Step, um sie hochzuladen.
        </div>
      </div>
    );
  }

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    // ein Schritt = ca. eine Thumbnail-Breite (128px + 8px gap)
    const step = 136;
    el.scrollBy({ left: dir * step * 3, behavior: "smooth" });
  };

  const currentPhoto = openIndex !== null ? photos[openIndex] : null;

  return (
    <>
      <div
        className="rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm"
        data-testid="calc-photo-slider"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-cyan-700" />
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-zinc-700">
              Lichtbilder · während der Kalkulation
            </div>
            <span className="text-[11px] text-zinc-500 font-mono">{photos.length}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="h-8 w-8 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 active:scale-95 transition flex items-center justify-center"
              aria-label="Vorherige Bilder"
              data-testid="calc-photo-slider-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="h-8 w-8 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 active:scale-95 transition flex items-center justify-center"
              aria-label="Nächste Bilder"
              data-testid="calc-photo-slider-next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Slider — horizontaler Scroller */}
        <div
          ref={scrollerRef}
          className="flex gap-2 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-1
                     [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5
                     [&::-webkit-scrollbar-thumb]:bg-zinc-300 [&::-webkit-scrollbar-thumb]:rounded-full"
          data-testid="calc-photo-slider-track"
        >
          {photos.map((p, i) => (
            <button
              key={p.id || i}
              type="button"
              onClick={() => openAt(i)}
              className="group relative shrink-0 snap-start
                         w-28 h-20 sm:w-32 sm:h-24
                         rounded-lg overflow-hidden border border-zinc-200
                         hover:ring-2 hover:ring-cyan-400 transition focus:outline-none focus:ring-2 focus:ring-cyan-500"
              title={p.filename || p.original_filename || `Foto #${i + 1}`}
              data-testid={`calc-photo-slider-item-${i}`}
            >
              <AuthImg
                path={p.storage_path}
                token={accessToken}
                alt={p.filename || p.original_filename || ""}
                className="w-full h-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/40 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Maximize2 className="h-4 w-4 text-white drop-shadow" />
              </div>
              <div className="absolute bottom-1 left-1 bg-white/85 text-[9px] font-mono font-bold text-zinc-700 px-1 py-0.5 rounded">
                #{i + 1}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Kompaktes, frei verschiebbares Mini-Panel (kein dunkler Hintergrund). */}
      {currentPhoto && (
        <DraggablePhotoPanel
          key={openIndex}
          photo={currentPhoto}
          index={openIndex}
          total={photos.length}
          token={accessToken}
          zoom={zoom}
          setZoom={setZoom}
          onPrev={() => openAt((openIndex - 1 + photos.length) % photos.length)}
          onNext={() => openAt((openIndex + 1) % photos.length)}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                          DraggablePhotoPanel                                */
/* -------------------------------------------------------------------------- */

function DraggablePhotoPanel({ photo, index, total, token, zoom, setZoom, onPrev, onNext, onClose }) {
  // Position des Panels (Pixel vom Viewport-Top-Left)
  const initial = () => {
    const w = Math.min(520, window.innerWidth * 0.42);
    const h = Math.min(window.innerHeight * 0.52, 380);
    return {
      x: Math.max(8, window.innerWidth - w - 32),  // standardmäßig rechts oben
      y: 96,
      width: w,
      height: h,
    };
  };
  const [pos, setPos] = useState(initial);
  const draggingRef = useRef(null);

  const onPointerDown = (e) => {
    // Nur Drag, wenn die Titelleiste selbst angefasst wird (keine Buttons)
    if (e.target.closest("button")) return;
    e.preventDefault();
    draggingRef.current = { startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp, { once: true });
  };
  const onPointerMove = (e) => {
    const d = draggingRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    setPos((p) => ({
      ...p,
      x: Math.max(0, Math.min(window.innerWidth - p.width, d.baseX + dx)),
      y: Math.max(0, Math.min(window.innerHeight - 40, d.baseY + dy)),
    }));
  };
  const onPointerUp = () => {
    draggingRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
  };

  return (
    <div
      className="fixed z-[80] bg-zinc-900 rounded-xl shadow-2xl overflow-hidden flex flex-col border border-zinc-700"
      style={{ left: pos.x, top: pos.y, width: pos.width, maxHeight: pos.height }}
      data-testid="calc-photo-mini-modal"
    >
      {/* Header — Drag-Handle */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-zinc-800 text-white text-xs cursor-move select-none"
        onPointerDown={onPointerDown}
        data-testid="calc-photo-mini-drag-handle"
      >
        <div className="font-mono">
          {index + 1} / {total} ·{" "}
          <span className="opacity-75">{photo.filename || `Foto #${index + 1}`}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
            className="h-7 w-7 rounded hover:bg-white/15 flex items-center justify-center"
            title="Verkleinern (−)"
            data-testid="calc-photo-mini-zoom-out"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <span className="font-mono text-[10px] w-10 text-center tabular-nums">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
            className="h-7 w-7 rounded hover:bg-white/15 flex items-center justify-center"
            title="Vergrößern (+)"
            data-testid="calc-photo-mini-zoom-in"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
          <div className="w-px h-4 bg-white/20 mx-1" />
          <button
            type="button"
            onClick={onClose}
            className="h-7 w-7 rounded hover:bg-rose-500/30 flex items-center justify-center"
            title="Schließen (Esc)"
            data-testid="calc-photo-mini-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Bild */}
      <div className="relative bg-black overflow-auto" style={{ minHeight: "200px" }}>
        {total > 1 && (
          <button
            type="button"
            onClick={onPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center"
            title="Vorheriges Foto (←)"
            data-testid="calc-photo-mini-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        <div className="flex items-center justify-center p-2 select-none">
          <AuthImg
            path={photo.storage_path}
            token={token}
            alt={photo.filename || ""}
            className="object-contain pointer-events-none"
            style={{
              maxHeight: `calc(${pos.height}px - 50px)`,
              maxWidth: `calc(${pos.width}px - 24px)`,
              transform: `scale(${zoom})`,
              transition: "transform 0.18s ease-out",
              transformOrigin: "center center",
            }}
          />
        </div>
        {total > 1 && (
          <button
            type="button"
            onClick={onNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center"
            title="Nächstes Foto (→)"
            data-testid="calc-photo-mini-next"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
